import type { EditorJsData, Homework, HomeworkItem } from '@/types'

/**
 * Пуст ли документ Editor.js по существу.
 *
 * Редактор нормализует пустое состояние в один пустой параграф, поэтому
 * проверка «blocks не пуст» всегда истинна — и пустой ответ уходил бы на
 * сервер как настоящий.
 */
export function editorJsDataIsEffectivelyEmpty(data: EditorJsData | undefined | null): boolean {
  if (data == null) return true

  const blocks = data.blocks
  if (!Array.isArray(blocks) || blocks.length === 0) return true

  if (blocks.length === 1 && blocks[0]?.type === 'paragraph') {
    const text = String((blocks[0] as { data?: { text?: string } }).data?.text ?? '').trim()
    return text.length === 0
  }

  return false
}

/**
 * Хелперы домашки 2.0.
 *
 * Задание и ответ живут в пунктах, а списки и превью по-прежнему показывают
 * одну строку — эти функции и есть мост между тем и другим.
 */

/** Пункты по порядку; у домашки их всегда минимум один */
export function homeworkItems(homework: Homework): HomeworkItem[] {
  return [...(homework.items ?? [])].sort((a, b) => a.position - b.position)
}

/**
 * Подпись одного пункта для списков.
 *
 * У пункта-задания текста задания нет вовсе (сервер кладёт `task_json = null`,
 * а `task_text` выводится из него), поэтому подписью служит заголовок самого
 * задания — иначе домашка целиком из заданий выглядела бы пустой строкой.
 */
export function homeworkItemLabel(item: HomeworkItem): string {
  return item.task_text?.trim() || item.exercise?.title?.trim() || ''
}

/** Краткий текст задания для списков: первый пункт плюс счётчик остальных */
export function homeworkTaskSummary(homework: Homework): string {
  const items = homeworkItems(homework)
  if (items.length === 0) return ''

  // Первый непустой: подписи может не быть и у текстового пункта (одна
  // картинка в задании), а голое «(+2)» не говорит вообще ни о чём
  const first = items.map(homeworkItemLabel).find((label) => label !== '') ?? ''
  if (items.length === 1) return first

  return first === '' ? '' : `${first} (+${items.length - 1})`
}

/** Краткий текст ответа для списков: собирается из отвеченных пунктов */
export function homeworkAnswerSummary(homework: Homework): string {
  return homeworkItems(homework)
    .map((item) => item.answer_text?.trim())
    .filter((text): text is string => Boolean(text))
    .join(' · ')
}

/** Есть ли что проверять: хоть один пункт отвечен либо домашка сдана */
export function homeworkHasAnswers(homework: Homework): boolean {
  return homeworkItems(homework).some((item) => Boolean(item.completed_at))
}

/** Один текстовый пункт — привычная «домашка просто текстом» */
export function singleTextItem(taskJson: EditorJsData): Array<{ type: 'text'; task_json: EditorJsData }> {
  return [{ type: 'text', task_json: taskJson }]
}

/** Пункт в редакторе: id есть только у сохранённых — по нему выживает ответ ученика */
export interface EditableHomeworkItem {
  id?: number
  key: string
  type: 'text' | 'exercise'
  task_json: EditorJsData
  /** Только у пункта-задания */
  exercise_id?: number | null
  /** Название задания для показа в редакторе — на сервер не уходит */
  exercise_title?: string | null
  /**
   * Слот выдачи: элемент темы программы ученика, из которого взято задание.
   *
   * Одного `exercise_id` серверу мало — то же задание банка стоит сразу в
   * нескольких темах, и без слота он выбирает первый попавшийся элемент, а
   * работа уходит в статистику чужой темы. `null` — задание взято из банка,
   * места в программе у него нет.
   */
  assigned_element_id?: number | null
}

function newKey(): string {
  return `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

export function emptyHomeworkItem(): EditableHomeworkItem {
  return {
    key: newKey(),
    type: 'text',
    task_json: { blocks: [], time: undefined, version: '2.0.0' },
  }
}

/**
 * Пункт-задание из строки пикера.
 *
 * `assignedElementId` берётся из той же строки, что и задание: пикер
 * показывает задание программы отдельной строкой на каждый элемент темы, и
 * потерять слот здесь значит отдать серверу голый `exercise_id`, по которому
 * он выберет место в программе наугад.
 */
export function exerciseHomeworkItem(
  exerciseId: number,
  title: string,
  assignedElementId: number | null = null,
): EditableHomeworkItem {
  return {
    key: newKey(),
    type: 'exercise',
    exercise_id: exerciseId,
    exercise_title: title,
    assigned_element_id: assignedElementId,
    task_json: { blocks: [], time: undefined, version: '2.0.0' },
  }
}

/** Тело запроса из пункта редактора: у каждого типа своё поле */
export function homeworkItemPayload(item: EditableHomeworkItem): {
  id?: number
  type: 'text' | 'exercise'
  task_json?: EditorJsData
  exercise_id?: number
  assigned_element_id?: number | null
} {
  if (item.type === 'exercise') {
    return {
      id: item.id,
      type: 'exercise',
      exercise_id: item.exercise_id ?? undefined,
      // Явный null, а не пропуск поля: у задания из банка слота нет, и
      // выдумывать его вместо сервера клиенту нечем
      assigned_element_id: item.assigned_element_id ?? null,
    }
  }

  return { id: item.id, type: 'text', task_json: item.task_json }
}
